import React, { ReactElement, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  MoreHorizontal,
  ArrowUpDown,
  Calendar,
  Signal,
  Activity,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { KanbanBoardType } from "@/hooks/services/pps/types/job-orders.type";

import { ScrollArea } from "@radix-ui/react-scroll-area";
import dayjs from "dayjs";

function KanbanBoard({
  columns,
  onDragEnd,
  children,
  setColumns,
}: {
  columns: any;
  onDragEnd: (value: DropResult) => void;
  children: ReactElement;
  setColumns: (value: any) => void;
}) {
  const [sortBy, setSortBy] = useState({ order: "", orderBy: "due-date" });

  const sortTasks = (
    column: KanbanBoardType<any>,
    sorted: { order: string; orderBy: string },
  ) => {
    const sortedTasks = [...column.tasks].sort((a, b) => {
      const statusA = a.status || "";
      const statusB = b.status || "";
      const dateA = dayjs(a.dueDate).isValid() ? dayjs(a.dueDate).valueOf() : 0;
      const dateB = dayjs(b.dueDate).isValid() ? dayjs(b.dueDate).valueOf() : 0;
      if (b.priority && a.priority) {
        if (sorted.order === "desc") {
          if (sorted.orderBy === "due-date") {
            return dateB - dateA;
          } else if (sorted.orderBy === "priority") {
            return b.priority - a.priority;
          } else if (sorted.orderBy === "status") {
            return statusB.localeCompare(statusA);
          }
        } else {
          if (sorted.orderBy === "due-date") {
            return dateA - dateB;
          } else if (sorted.orderBy === "priority") {
            return a.priority - b.priority;
          } else if (sorted.orderBy === "status") {
            return statusA.localeCompare(statusB);
          }
        }
      }

      return 0;
    });

    return sortedTasks;
  };

  const handleDropdownChange = (value: string, col: any) => {
    const sorted =
      sortBy.orderBy === value
        ? { order: sortBy.order === "desc" ? "asc" : "desc", orderBy: value }
        : { order: "desc", orderBy: value };

    const newColumns = [...columns];

    setColumns(
      newColumns.map((column) => ({
        ...column,
        tasks: col.id === column.id ? sortTasks(column, sorted) : column.tasks,
      })),
    );

    setSortBy(sorted);
  };

  return (
    <ScrollArea className="aero:h-3/4 aero:w-80">
      <>
        {/* DRAG AND DROP SECTION */}
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="aero:flex aero:gap-4 aero:p-4">
            {columns.map((column: any) => (
              <div key={column.id} className="aero:flex-1 aero:min-w-[320px]">
                {/* Header Board */}
                <div className="aero:flex aero:items-center aero:justify-between aero:mb-4 aero:pl-2">
                  <div className="aero:flex aero:items-center">
                    <span className={`aero:mr-2 ${column.color}`}>●</span>
                    <span className="aero:!font-medium">{column.title}</span>
                    <Badge variant="secondary" className="aero:ml-2">
                      {column.tasks.length}
                    </Badge>
                  </div>
                  <div className="aero:flex aero:items-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          intent="custom"
                          design="text"
                          size="iconMd"
                          className="aero:h-8 aero:w-8"
                        >
                          <MoreHorizontal className="aero:h-4 aero:w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="aero:w-48">
                        <DropdownMenuLabel>Column Actions</DropdownMenuLabel>
                        <DropdownMenuGroup>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <ArrowUpDown className="aero:mr-2 aero:h-4 aero:w-4" />
                              Sort Cards
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuRadioGroup
                                value={sortBy.orderBy}
                                onValueChange={(value) =>
                                  handleDropdownChange(value, column)
                                }
                              >
                                <DropdownMenuRadioItem value="due-date">
                                  <Calendar className="aero:mr-2 aero:h-4 aero:w-4" />
                                  Due Date
                                </DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="priority">
                                  <Signal className="aero:mr-2 aero:h-4 aero:w-4" />
                                  Priority
                                </DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="status">
                                  <Activity className="aero:mr-2 aero:h-4 aero:w-4" />
                                  Status
                                </DropdownMenuRadioItem>
                              </DropdownMenuRadioGroup>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Card Group */}
                <div className="aero:space-y-4">
                  <Droppable droppableId={column.id}>
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="aero:min-h-[500px] aero:grid"
                      >
                        <Card
                          className="aero:bg-muted/50 aero:p-0"
                          key={column.id}
                        >
                          <CardContent className="aero:p-2">
                            {column.tasks.map((task: any, index: number) => (
                              <Draggable
                                draggableId={task.id.toString()}
                                index={index}
                                key={task.id}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    key={task.id}
                                  >
                                    {React.cloneElement(children, { task })}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                          </CardContent>
                        </Card>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              </div>
            ))}
          </div>
        </DragDropContext>
      </>
    </ScrollArea>
  );
}

export default KanbanBoard;
